import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, LayoutDashboard, ShoppingBag, HelpCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/api'; // Import API to fix missing roles

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Retrieve auth state directly from storage
  const token = localStorage.getItem('token');
  const rawRole = localStorage.getItem('role');
  const username = localStorage.getItem('username');

  // Handle Scroll Effect
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // --- SELF-HEALING: Fetch Role if missing ---
  useEffect(() => {
    if (token && !rawRole) {
      console.log("Token found but Role missing. Fetching profile...");
      api.get('auth/profile/')
        .then(res => {
          if (res.data.role) {
            localStorage.setItem('role', res.data.role);
            localStorage.setItem('username', res.data.username);
            // Reload to apply the fixed role
            window.location.reload();
          }
        })
        .catch(err => {
          console.error("Session invalid:", err);
          // If token is invalid, clear it
          localStorage.clear();
          navigate('/login');
        });
    }
  }, [token, rawRole, navigate]);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
    setIsOpen(false);
  };

  // --- ROBUST ROUTING LOGIC ---
  const getDashboardLink = () => {
    // 1. Get role and ensuring it is a string for comparison
    const r = String(rawRole); 
    
    // 2. Check for specific roles
    if (r === '1') return 'https://revenuesetters.com/api/admin'; 
    if (r === '2') return '/company/dashboard';
    if (r === '3') return '/setter/dashboard';
    
    // 3. NEW FALLBACK: Send to the Smart Redirector instead of Marketplace
    // This allows App.js to check the role one last time or redirect to login
    return '/dashboard'; 
  };

  const getDashboardLabel = () => {
    const r = String(rawRole);
    if (r === '1') return 'Admin Panel';
    if (r === '2') return 'My Dashboard';
    if (r === '3') return 'Setter Portal';
    return 'Dashboard';
  };

  return (
    <nav 
      className={`sticky top-0 w-full z-50 transition-all duration-300 font-[Poppins] ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-md py-2' : 'bg-white py-4 shadow-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          
          {/* --- LOGO --- */}
          <Link to="/" className="flex-shrink-0 flex items-center">
            <img 
              src="/logo.png" 
              alt="Revenue Setters" 
              className="h-auto w-auto max-h-[50px] md:max-h-[80px] max-w-[180px] md:max-w-[300px] lg:max-w-[400px] object-contain transition-all duration-300"
            />
          </Link>

          {/* --- DESKTOP MENU --- */}
          <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
            <NavLink to="/marketplace" label="Marketplace" icon={<ShoppingBag size={18} />} active={location.pathname === '/marketplace'} />
            <NavLink to="/how-it-works" label="How It Works" icon={<HelpCircle size={18} />} active={location.pathname === '/how-it-works'} />

            {token ? (
              <>
                <NavLink 
                  to={getDashboardLink()} 
                  label={getDashboardLabel()} 
                  icon={<LayoutDashboard size={18} />} 
                  active={location.pathname.includes('dashboard') || location.pathname.includes('admin')}
                />

                <div className="flex items-center pl-6 border-l border-gray-200 ml-2 space-x-4">
                  <div className="text-right hidden lg:block">
                    <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Welcome</p>
                    <p className="text-sm font-bold text-gray-800 leading-none">{username || 'User'}</p>
                  </div>
                  <button 
                    onClick={handleLogout}
                    className="flex items-center text-gray-400 hover:text-red-600 hover:bg-red-50 p-2 rounded-full transition-all duration-200 group"
                    title="Sign Out"
                  >
                    <LogOut size={20} className="group-hover:scale-110 transition-transform" />
                  </button>
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-3">
                <Link to="/login" className="px-4 py-2 text-gray-600 hover:text-primary font-bold text-sm transition-colors">
                  Log In
                </Link>
                <Link 
                  to="/register" 
                  className="bg-primary hover:bg-blue-700 text-white px-5 py-2.5 rounded-full font-bold text-sm shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5"
                >
                  Get Started
                </Link>
              </div>
            )}
          </div>

          {/* --- MOBILE BURGER --- */}
          <div className="md:hidden flex items-center">
            <button 
              onClick={() => setIsOpen(!isOpen)} 
              className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-primary focus:outline-none transition-colors"
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* --- MOBILE DRAWER --- */}
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-gray-100 overflow-hidden shadow-xl"
          >
            <div className="px-4 pt-4 pb-8 space-y-3">
              {token && (
                <div className="flex items-center p-4 bg-gray-50 rounded-xl mb-6 border border-gray-100">
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-primary font-bold mr-3 border border-blue-200">
                    {username?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">{username || 'User'}</p>
                    <p className="text-xs text-gray-500 font-medium">
                      {String(rawRole) === '1' ? 'Administrator' : String(rawRole) === '2' ? 'Company Account' : 'Setter Account'}
                    </p>
                  </div>
                </div>
              )}

              <MobileNavLink to="/marketplace" label="Marketplace" icon={<ShoppingBag size={20} />} onClick={() => setIsOpen(false)} />
              <MobileNavLink to="/how-it-works" label="How It Works" icon={<HelpCircle size={20} />} onClick={() => setIsOpen(false)} />
              
              {token ? (
                <>
                  <MobileNavLink 
                    to={getDashboardLink()} 
                    label={getDashboardLabel()} 
                    icon={<LayoutDashboard size={20} />} 
                    onClick={() => setIsOpen(false)} 
                    active={true}
                  />
                  <button 
                    onClick={handleLogout}
                    className="w-full flex items-center px-4 py-3 text-red-600 font-bold hover:bg-red-50 rounded-xl transition-colors mt-4"
                  >
                    <LogOut size={20} className="mr-3" /> Sign Out
                  </button>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <Link 
                    to="/login" 
                    onClick={() => setIsOpen(false)}
                    className="flex justify-center py-3 rounded-xl border-2 border-gray-200 text-gray-700 font-bold hover:border-gray-300 hover:bg-gray-50 transition-all"
                  >
                    Log In
                  </Link>
                  <Link 
                    to="/register" 
                    onClick={() => setIsOpen(false)}
                    className="flex justify-center py-3 rounded-xl bg-primary text-white font-bold hover:bg-blue-700 shadow-md transition-all"
                  >
                    Sign Up
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// --- HELPER COMPONENTS ---

const NavLink = ({ to, label, icon, active }) => {
  // Check if it is an External link (Django Admin or Absolute URL)
  const isExternal = to.startsWith('http') || to.startsWith('/api');

  if (isExternal) {
    return (
      <a 
        href={to}
        className={`flex items-center font-medium text-sm transition-all duration-200 group ${
          active ? 'text-primary font-bold' : 'text-gray-500 hover:text-gray-900'
        }`}
      >
        {icon && <span className={`mr-2 transition-transform duration-200 ${active ? 'scale-110' : 'group-hover:scale-110'}`}>{icon}</span>}
        {label}
      </a>
    );
  }

  return (
    <Link 
      to={to} 
      className={`flex items-center font-medium text-sm transition-all duration-200 group ${
        active ? 'text-primary font-bold' : 'text-gray-500 hover:text-gray-900'
      }`}
    >
      {icon && <span className={`mr-2 transition-transform duration-200 ${active ? 'scale-110' : 'group-hover:scale-110'}`}>{icon}</span>}
      {label}
    </Link>
  );
};

const MobileNavLink = ({ to, label, icon, onClick, active }) => {
  const isExternal = to.startsWith('http') || to.startsWith('/api');

  if (isExternal) {
    return (
      <a 
        href={to} 
        className={`flex items-center px-4 py-3 rounded-xl font-semibold transition-all ${
            active ? 'bg-blue-50 text-primary' : 'text-gray-700 hover:bg-gray-50 hover:text-primary'
        }`}
      >
        {icon && <span className="mr-3 opacity-70">{icon}</span>}
        {label}
      </a>
    );
  }

  return (
    <Link 
      to={to} 
      onClick={onClick}
      className={`flex items-center px-4 py-3 rounded-xl font-semibold transition-all ${
          active ? 'bg-blue-50 text-primary' : 'text-gray-700 hover:bg-gray-50 hover:text-primary'
      }`}
    >
      {icon && <span className="mr-3 opacity-70">{icon}</span>}
      {label}
    </Link>
  );
};

export default Navbar;